// Field schemas describe each Home section's editable structure.
// Field types: "text", "textarea", "image", "icon", "url"
// Lists use { type: "list", item: <schema> }  where item describes each row's fields.

const ICON_OPTIONS = [
  "Radar", "Brain", "Network", "Boxes", "Glasses", "Zap",
  "Cpu", "LayoutDashboard", "Orbit", "Wifi", "MonitorCog",
  "Sparkles", "Workflow", "Layers", "ShieldCheck", "Target", "Repeat",
  "Factory", "HardHat", "Truck", "Building2", "Bolt", "Briefcase",
  "ShieldAlert", "Warehouse", "Users", "Stethoscope",
];

const HOME_SCHEMAS = {
  hero: {
    title: "Hero",
    fields: [
      { name: "overline", label: "Overline", type: "text" },
      { name: "title_pre", label: "Title — text before highlight", type: "text" },
      { name: "title_highlight", label: "Title — highlighted text (gradient)", type: "text" },
      { name: "title_post", label: "Title — text after highlight", type: "text" },
      { name: "paragraph1", label: "Paragraph 1", type: "textarea" },
      { name: "paragraph2", label: "Paragraph 2", type: "textarea" },
      { name: "cta_primary_label", label: "Primary CTA label", type: "text" },
      { name: "cta_primary_link", label: "Primary CTA link", type: "url" },
      { name: "cta_secondary_label", label: "Secondary CTA label", type: "text" },
      { name: "cta_secondary_link", label: "Secondary CTA link", type: "url" },
      { name: "video_url", label: "Hero video URL", type: "image" },
      { name: "bg_image", label: "Background image", type: "image" },
      { name: "badge_label", label: "Badge label (small)", type: "text" },
      { name: "badge_text", label: "Badge text", type: "text" },
      {
        name: "stats", label: "Stats (3 items)", type: "list",
        item: [
          { name: "key", label: "Number / headline", type: "text" },
          { name: "value", label: "Caption", type: "text" },
        ],
      },
    ],
  },

  "operational-intelligence": {
    title: "Section 2 — Operational Intelligence",
    fields: [
      { name: "overline", label: "Overline", type: "text" },
      { name: "title_pre", label: "Title — before highlight", type: "text" },
      { name: "title_highlight", label: "Title — highlighted", type: "text" },
      { name: "paragraph", label: "Paragraph", type: "textarea" },
      { name: "card_title", label: "Card heading", type: "text" },
      {
        name: "questions", label: "Questions list", type: "list",
        item: [{ name: "_value", label: "Question", type: "text" }],
        primitive: true,
      },
      { name: "answer_label", label: "Answer block label", type: "text" },
      { name: "answer_text", label: "Answer block text", type: "textarea" },
    ],
  },

  "connected-systems": {
    title: "Section 3 — Connected Systems",
    fields: [
      { name: "overline", label: "Overline", type: "text" },
      { name: "title_pre", label: "Title — before highlight", type: "text" },
      { name: "title_highlight", label: "Title — highlighted", type: "text" },
      { name: "paragraph1", label: "Paragraph 1", type: "textarea" },
      { name: "paragraph2", label: "Paragraph 2", type: "textarea" },
      { name: "conclusion_pre", label: "Conclusion — start", type: "text" },
      { name: "conclusion_old", label: "Conclusion — 'old' (greyed)", type: "text" },
      { name: "conclusion_mid", label: "Conclusion — middle", type: "text" },
      { name: "conclusion_new", label: "Conclusion — 'new' (gradient)", type: "text" },
      {
        name: "tiles", label: "Tiles (4 images)", type: "list",
        item: [
          { name: "img", label: "Image", type: "image" },
          { name: "label", label: "Label", type: "text" },
        ],
      },
    ],
  },

  framework: {
    title: "Section 4 — Intelligence Framework",
    fields: [
      { name: "overline", label: "Overline", type: "text" },
      { name: "title_pre", label: "Title — before highlight", type: "text" },
      { name: "title_highlight", label: "Title — highlighted", type: "text" },
      { name: "paragraph", label: "Paragraph", type: "textarea" },
      {
        name: "cards", label: "Framework cards (6 layers)", type: "list",
        item: [
          { name: "layer", label: "Layer name", type: "text" },
          { name: "title", label: "Card title", type: "text" },
          { name: "desc", label: "Description", type: "textarea" },
          { name: "outcome", label: "Outcome", type: "textarea" },
          { name: "icon", label: "Icon", type: "icon" },
        ],
      },
    ],
  },

  "what-we-build": {
    title: "Section 5 — What We Build",
    fields: [
      { name: "overline", label: "Overline", type: "text" },
      { name: "title_pre", label: "Title — before highlight", type: "text" },
      { name: "title_highlight", label: "Title — highlighted", type: "text" },
      { name: "title_post", label: "Title — after highlight", type: "text" },
      {
        name: "cards", label: "Build cards", type: "list",
        item: [
          { name: "title", label: "Title", type: "text" },
          { name: "desc", label: "Description", type: "textarea" },
          { name: "icon", label: "Icon", type: "icon" },
          { name: "features", label: "Features (one per line)", type: "lines" },
        ],
      },
    ],
  },

  outcomes: {
    title: "Section 6 — Outcomes",
    fields: [
      { name: "overline", label: "Overline", type: "text" },
      { name: "title_pre", label: "Title — before highlight", type: "text" },
      { name: "title_highlight", label: "Title — highlighted", type: "text" },
      { name: "title_post", label: "Title — after highlight", type: "text" },
      { name: "paragraph", label: "Paragraph", type: "textarea" },
      { name: "image_url", label: "Side image", type: "image" },
      {
        name: "items", label: "Outcome items", type: "list",
        item: [{ name: "_value", label: "Outcome", type: "text" }],
        primitive: true,
      },
    ],
  },

  capabilities: {
    title: "Section 7 — Capabilities",
    fields: [
      { name: "overline", label: "Overline", type: "text" },
      { name: "title_pre", label: "Title — before highlight", type: "text" },
      { name: "title_highlight", label: "Title — highlighted", type: "text" },
      { name: "paragraph", label: "Paragraph", type: "textarea" },
      { name: "chips_label", label: "Chips section label", type: "text" },
      {
        name: "chips", label: "Capability chips", type: "list",
        item: [
          { name: "title", label: "Title", type: "text" },
          { name: "desc", label: "Description", type: "textarea" },
        ],
      },
    ],
  },

  "built-for": {
    title: "Section 8 — Built For",
    fields: [
      { name: "overline", label: "Overline", type: "text" },
      { name: "title_pre", label: "Title — before highlight", type: "text" },
      { name: "title_highlight", label: "Title — highlighted", type: "text" },
      { name: "paragraph", label: "Paragraph", type: "textarea" },
      {
        name: "items", label: "Use case items", type: "list",
        item: [{ name: "_value", label: "Use case", type: "text" }],
        primitive: true,
      },
    ],
  },

  "why-ngxp": {
    title: "Section 9 — Why NGXP",
    fields: [
      { name: "overline", label: "Overline", type: "text" },
      { name: "title_pre", label: "Title — before highlight", type: "text" },
      { name: "title_highlight", label: "Title — highlighted", type: "text" },
      {
        name: "cards", label: "Why-NGXP cards", type: "list",
        item: [
          { name: "title", label: "Title", type: "text" },
          { name: "desc", label: "Description", type: "textarea" },
          { name: "icon", label: "Icon", type: "icon" },
        ],
      },
    ],
  },

  transformation: {
    title: "Section 10 — Intelligent Transformation",
    fields: [
      { name: "overline", label: "Overline", type: "text" },
      { name: "image_url", label: "Section image", type: "image" },
      { name: "title_pre", label: "Title — before highlight", type: "text" },
      { name: "title_highlight", label: "Title — highlighted", type: "text" },
      { name: "paragraph", label: "Paragraph", type: "textarea" },
      {
        name: "timeline", label: "Timeline (last item is highlighted)", type: "list",
        item: [
          { name: "label", label: "Label (e.g. 'Today')", type: "text" },
          { name: "value", label: "Value (e.g. 'Software')", type: "text" },
        ],
      },
    ],
  },

  "final-cta": {
    title: "Section 11 — Final CTA",
    fields: [
      { name: "title", label: "Heading", type: "textarea" },
      { name: "paragraph", label: "Paragraph", type: "textarea" },
      { name: "cta_primary_label", label: "Primary CTA label", type: "text" },
      { name: "cta_primary_link", label: "Primary CTA link", type: "url" },
      { name: "cta_secondary_label", label: "Secondary CTA label", type: "text" },
      { name: "cta_secondary_link", label: "Secondary CTA link", type: "url" },
      { name: "tagline", label: "Tagline (italic)", type: "text" },
    ],
  },
};

export { HOME_SCHEMAS, ICON_OPTIONS };
